import { useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetcher } from "@/utils/api";
import { buildImageUrl } from "@/utils/imageUtils";
import { getTokens } from "@/utils/token";
import dayjs from "dayjs";

// ============================================
// QUERY KEYS - مركزية لتجنب التكرار والتعارض
// ============================================
export const QUERY_KEYS = {
  categories: ["categories", "pos"] as const,
  servicesAll: ["services", "all"] as const,
  subscriptionsAll: ["subscriptions", "all"] as const,
  coupons: ["coupons"] as const,
  currentSession: ["current-session"] as const,
  sessionInvoices: (sessionId: number) => ["session-invoices", sessionId] as const,
  orders: (page: number, date: string) => ["orders", page, date] as const,
  invoices: (page: number) => ["invoices", page] as const,
} as const;

// ============================================
// CACHE TIMES - موحدة ومركزية
// ============================================
const CACHE_TIMES = {
  static: {
    staleTime: 5 * 60 * 1000,      // 5 دقائق - البيانات تعتبر fresh
    gcTime: 60 * 60 * 1000,        // ساعة - تبقى في الذاكرة
  },
  dynamic: {
    staleTime: 30 * 1000,          // 30 ثانية
    gcTime: 10 * 60 * 1000,        // 10 دقائق
  },
  critical: {
    staleTime: 0,                  // دائماً stale - يُعاد الجلب عند الطلب
    gcTime: 5 * 60 * 1000,         // 5 دقائق
  },
} as const;

// ============================================
// REFRESH INTERVALS
// ============================================
const REFRESH_INTERVALS = {
  critical: 30 * 1000,    // 30 ثانية للبيانات الحيوية
  static: 5 * 60 * 1000,  // 5 دقائق للبيانات الثابتة
} as const;

// ============================================
// IMAGE TRANSFORMER - لتوحيد معالجة الصور
// ============================================
const transformWithImages = <T extends { image?: string | null }>(
  items: T[]
): T[] =>
  items.map((item) => ({
    ...item,
    image: buildImageUrl(item.image || null),
  }));

// ============================================
// MAIN HOOK
// ============================================
const usePrefetchStoreData = () => {
  const queryClient = useQueryClient();
  const intervalsRef = useRef<{
    critical: NodeJS.Timeout | null;
    static: NodeJS.Timeout | null;
  }>({ critical: null, static: null });

  const { access } = getTokens();

  // ==========================================
  // البيانات الثابتة (Categories, Services, Subscriptions)
  // ==========================================
  const prefetchStaticData = useCallback(async () => {
    const pageLimit = "?page_limit=100";

    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: QUERY_KEYS.categories,
        queryFn: async () => {
          const res: any = await fetcher(`/store/categories/${pageLimit}`);
          const categories = res.results || res || [];
          return transformWithImages(categories);
        },
        ...CACHE_TIMES.static,
      }),

      queryClient.prefetchQuery({
        queryKey: QUERY_KEYS.servicesAll,
        queryFn: async () => {
          const res: any = await fetcher(`/store/services/${pageLimit}`);
          const services = res.results || res || [];
          return transformWithImages(services);
        },
        ...CACHE_TIMES.static,
      }),

      queryClient.prefetchQuery({
        queryKey: QUERY_KEYS.subscriptionsAll,
        queryFn: async () => {
          const res: any = await fetcher(`/store/subscriptions/${pageLimit}`);
          const subscriptions = res.results || res || [];
          return transformWithImages(subscriptions);
        },
        ...CACHE_TIMES.static,
      }),
    ]);
  }, [queryClient]);

  // ==========================================
  // البيانات الديناميكية (Sessions, Coupons)
  // ==========================================
  const prefetchDynamicData = useCallback(async () => {
    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: QUERY_KEYS.coupons,
        queryFn: async () => {
          const res: any = await fetcher("/store/coupons/");
          return res.results || res || [];
        },
        ...CACHE_TIMES.dynamic,
      }),

      queryClient.prefetchQuery({
        queryKey: QUERY_KEYS.currentSession,
        queryFn: async () => {
          const res: any = await fetcher("/user/sessions/");

          // معالجة أشكال مختلفة من الاستجابة
          let sessions: any[] = [];
          if (Array.isArray(res)) {
            sessions = res;
          } else if (res?.results && Array.isArray(res.results)) {
            sessions = res.results;
          } else if (typeof res === "object" && res?.id) {
            sessions = [res];
          }

          // البحث عن الجلسة النشطة
          return sessions.find((s) => !s.ended_at) ?? null;
        },
        ...CACHE_TIMES.dynamic,
      }),
    ]);
  }, [queryClient]);

  // ==========================================
  // فواتير الجلسة النشطة
  // ==========================================
  const prefetchSessionInvoices = useCallback(async () => {
    const currentSession = queryClient.getQueryData<{ id: number; ended_at?: string }>(
      QUERY_KEYS.currentSession
    );

    if (!currentSession?.id || currentSession.ended_at) return;

    await queryClient.prefetchQuery({
      queryKey: QUERY_KEYS.sessionInvoices(currentSession.id),
      queryFn: async () => {
        try {
          // جلب جميع صفحات الفواتير (مثل useSessionInvoices) لتجنب الكتابة الجزئية على الكاش
          const firstRes: any = await fetcher(
            `/store/invoices/?session=${currentSession.id}`
          );
          let allInvoices: any[] = firstRes.results || [];

          // تتبع الصفحات التالية
          let nextUrl: string | null = firstRes.next || null;
          while (nextUrl) {
            try {
              const nextRes: any = await fetcher(nextUrl);
              allInvoices = allInvoices.concat(nextRes.results || []);
              nextUrl = nextRes.next || null;
            } catch {
              break;
            }
          }

          const totals = allInvoices.reduce(
            (acc: any, inv: any) => ({
              total_sales: acc.total_sales + (inv.cash || 0) + (inv.visa || 0),
              cash_total: acc.cash_total + (inv.cash || 0),
              card_total: acc.card_total + (inv.visa || 0),
            }),
            { total_sales: 0, cash_total: 0, card_total: 0 }
          );

          return { invoices: allInvoices, ...totals, session_id: currentSession.id };
        } catch {
          return {
            invoices: [],
            total_sales: 0,
            cash_total: 0,
            card_total: 0,
            session_id: currentSession.id,
          };
        }
      },
      ...CACHE_TIMES.critical,
    });
  }, [queryClient]);

  // ==========================================
  // البيانات الحيوية (Orders, Invoices)
  // ==========================================
  const prefetchCriticalData = useCallback(async () => {
    const today = dayjs().format("YYYY-MM-DD");

    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: QUERY_KEYS.orders(1, today),
        queryFn: async () => {
          const res: any = await fetcher(`/store/orders/?page=1&date=${today}`);
          return res || {};
        },
        ...CACHE_TIMES.critical,
      }),

      queryClient.prefetchQuery({
        queryKey: QUERY_KEYS.invoices(1),
        queryFn: async () => {
          const res: any = await fetcher("/store/invoices/?page=1");
          return res || {};
        },
        ...CACHE_TIMES.critical,
      }),
    ]);
  }, [queryClient]);

  // ==========================================
  // التحميل الأولي - Sequential للأولويات
  // ==========================================
  useEffect(() => {
    if (!access) return;

    const loadInitialData = async () => {
      try {
        // 1. البيانات الأساسية أولاً (مطلوبة للعرض)
        await prefetchStaticData();

        // 2. البيانات الديناميكية (الجلسة)
        await prefetchDynamicData();

        // 3. انتظار قليل ثم فواتير الجلسة
        await prefetchSessionInvoices();

        // 4. الطلبات والفواتير
        await prefetchCriticalData();
      } catch {
        // Silent - سيتم إعادة المحاولة تلقائياً
      }
    };

    loadInitialData();
  }, [
    access,
    prefetchStaticData,
    prefetchDynamicData,
    prefetchSessionInvoices,
    prefetchCriticalData,
  ]);

  // ==========================================
  // تحديث دوري للبيانات الحيوية
  // ==========================================
  useEffect(() => {
    if (!access) return;

    intervalsRef.current.critical = setInterval(async () => {
      try {
        await prefetchDynamicData();
        await prefetchSessionInvoices();
        await prefetchCriticalData();
      } catch {
        // Silent
      }
    }, REFRESH_INTERVALS.critical);

    return () => {
      if (intervalsRef.current.critical) {
        clearInterval(intervalsRef.current.critical);
      }
    };
  }, [access, prefetchDynamicData, prefetchSessionInvoices, prefetchCriticalData]);

  // ==========================================
  // تحديث دوري للبيانات الثابتة
  // ==========================================
  useEffect(() => {
    if (!access) return;

    intervalsRef.current.static = setInterval(async () => {
      try {
        await prefetchStaticData();
      } catch {
        // Silent
      }
    }, REFRESH_INTERVALS.static);

    return () => {
      if (intervalsRef.current.static) {
        clearInterval(intervalsRef.current.static);
      }
    };
  }, [access, prefetchStaticData]);

  // ==========================================
  // تحديث عند العودة للنافذة
  // ==========================================
  useEffect(() => {
    if (!access) return;

    const handleVisibilityChange = () => {
      if (document.hidden) return;

      // تحديث البيانات الحيوية فوراً
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.currentSession });
      queryClient.invalidateQueries({ queryKey: ["session-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.coupons });

      // تحديث البيانات الثابتة بتأخير
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.servicesAll });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.subscriptionsAll });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.categories });
      }, 500);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, [access, queryClient]);

  // ==========================================
  // تنظيف عند Unmount
  // ==========================================
  useEffect(() => {
    return () => {
      Object.values(intervalsRef.current).forEach((interval) => {
        if (interval) clearInterval(interval);
      });
    };
  }, []);
};

export default usePrefetchStoreData;
